import { ContainerHtml, visualNodes } from "@dinahmoe/dmafjs-visual";
import { bridge, env } from "@dinahmoe/dmafjs-core";
import { nodes } from "@dinahmoe/dmafjs-core";
// import GUI from "lil-gui";

/**
 * Ease-in-out cubic - S-curve that accelerates then decelerates
 * @param {number} t - Progress from 0 to 1
 * @returns {number} - Eased progress from 0 to 1
 */
function easeInOutCubic(t) {
  return t < 0.5
    ? 4 * t * t * t
    : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * Calculate profile-aware angular distribution for cases.
 *
 * @param {number} numCases - Number of cases to distribute
 * @param {number} arcStart - Start angle of available arc (radians)
 * @param {number} arcEnd - End angle of available arc (radians)
 * @param {number} aspectRatio - Card width/height ratio (e.g., 2.25 means 2.25x wider than tall)
 *                               1 = uniform distribution, higher = more clustering at horizontal
 * @returns {number[]} - Array of angles for each case
 */
function distributeByProfile(numCases, arcStart, arcEnd, aspectRatio) {
  if (numCases === 0) return [];
  if (numCases === 1) return [(arcStart + arcEnd) / 2];

  // Normalize arc (handle wrap-around)
  let arcEndNorm = arcEnd;
  if (arcEndNorm < arcStart) arcEndNorm += Math.PI * 2;

  // Calculate density function samples across the arc
  const steps = 360;
  const arcSpan = arcEndNorm - arcStart;
  const dTheta = arcSpan / steps;

  // Build cumulative capacity (inverse of profile = how many cards fit)
  const cumulative = [0];
  let totalCapacity = 0;

  for (let i = 0; i < steps; i++) {
    const theta = arcStart + i * dTheta;
    // Profile: aspectRatio * |sin| + |cos|
    // At horizontal (θ=0): profile = 1 (minimum)
    // At vertical (θ=π/2): profile = aspectRatio (maximum)
    const profile = aspectRatio * Math.abs(Math.sin(theta)) + Math.abs(Math.cos(theta));
    // Density: inverse of profile (more cards fit where profile is smaller)
    const density = 1 / profile;
    totalCapacity += density * dTheta;
    cumulative.push(totalCapacity);
  }

  // Place cases at equal capacity intervals
  const capacityPerCase = totalCapacity / numCases;
  const angles = [];

  for (let i = 0; i < numCases; i++) {
    // Center each case in its capacity slice
    const targetCapacity = (i + 0.5) * capacityPerCase;

    // Find angle where cumulative capacity reaches target
    for (let j = 0; j < steps; j++) {
      if (cumulative[j + 1] >= targetCapacity) {
        // Linear interpolation for precision
        const t = (targetCapacity - cumulative[j]) / (cumulative[j + 1] - cumulative[j]);
        let angle = arcStart + (j + t) * dTheta;
        // Normalize to 0-2π
        if (angle >= Math.PI * 2) angle -= Math.PI * 2;
        angles.push(angle);
        break;
      }
    }
  }

  return angles;
}

/**
 * Calculate the dead zone angle based on viewport aspect ratio.
 * Dead zones push case tiles away from horizontal extremes (0 and PI)
 * on portrait-oriented screens.
 */
function calculateDeadZone(width, height, threshold, strength, curve) {
  const ratio = width / height;
  if (ratio >= threshold) return 0;

  let t = (threshold - ratio) / threshold;
  t = Math.min(Math.max(t, 0), 1);
  t = Math.pow(t, curve);

  return t * strength;
}

/**
 * Remap an angle to avoid dead zones around the horizontal axis (0 and PI).
 * Each semicircle is compressed to exclude the dead zone regions,
 * pushing tiles toward the top and bottom.
 */
function remapAngleDeadZone(angle, deadZone) {
  if (deadZone <= 0) return angle;

  angle = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);

  const availableHalf = Math.PI - 2 * deadZone;
  if (availableHalf <= 0) return angle;

  const scale = availableHalf / Math.PI;

  if (angle < Math.PI) {
    return deadZone + angle * scale;
  } else {
    return Math.PI + deadZone + (angle - Math.PI) * scale;
  }
}

/**
 * NetworkContainerEnvironment
 *
 * POSITIONING ARCHITECTURE:
 * - ALL positioning logic is handled in this environment implementation
 * - All position calculations are in browser pixels
 * - Positions stored in: eNode._categoryPositions Map (uid -> { x, y })
 * - Positions applied via: CSS transforms (element.style.transform)
 *
 * COORDINATE SYSTEM:
 * - Container center: (0, 0) in layout coordinates
 * - Categories positioned in elliptical layout around center
 * - Cases positioned radially around expanded category
 * - CSS transforms handle actual DOM positioning
 */
class NetworkContainerEnvironment extends ContainerHtml {
  handlers = this.extendParentHandlers({
    enableAnimations: { handler: this.setEnableAnimations, type: "boolean", default: true },
    clear_cases: this.clearCases,
  });

  setProps(eNode) {
    super.setProps(eNode);

    // Animation state — mouse positions initialized lazily in animate()
    // because size props may be percentage strings before ResizeObserver resolves
    eNode._time = 0;
    eNode._mouseX = 0;
    eNode._mouseY = 0;
    eNode._smoothMouseX = NaN;
    eNode._smoothMouseY = NaN;
    eNode._hasMouseInput = false;
    eNode._animationFrameId = null;
    eNode._isPaused = false;

    // Pause when off-screen
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      eNode._isPaused = !entry.isIntersecting;
    }, { threshold: 0 });
    intersectionObserver.observe(eNode.element);
    eNode._intersectionObserver = intersectionObserver;

    // Ensure pointer events are enabled and touch drag works on mobile.
    // touchAction 'none' is required — scroll is forwarded manually during hold period.
    eNode.element.style.pointerEvents = 'auto';
    eNode.element.style.touchAction = 'none';

    // Track category divs
    eNode._categoryElements = new Map(); // uid -> { element, data, targetX, targetY }

    // POSITIONING: Store all category positions in BROWSER PIXELS
    // This is the single source of truth for category layout
    eNode._categoryPositions = new Map(); // uid -> { x, y } in browser pixels

    // Per-category expansion state - each category has isolated animation state
    eNode._categoryExpansionState = new Map(); // categoryUid -> { startTime, centeringProgress, expansionProgress, caseAngles, caseAnimationData }
    eNode._caseLines = []; // SVG lines for cases
    eNode._caseElements = new Map(); // uid -> { element, line, progress }

    // Create SVG for connection lines
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.style.position = "absolute";
    svg.style.top = "0";
    svg.style.left = "0";
    svg.style.width = "100%";
    svg.style.height = "100%";
    svg.style.pointerEvents = "none";
    svg.style.zIndex = "0";
    svg.style.overflow = "visible";
    svg.style.transform = "translateZ(0)";

    // Create mask to hide lines under cards
    const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    const mask = document.createElementNS("http://www.w3.org/2000/svg", "mask");
    mask.setAttribute("id", "card-mask");
    mask.setAttribute("maskUnits", "userSpaceOnUse");
    mask.setAttribute("x", "-5000");
    mask.setAttribute("y", "-5000");
    mask.setAttribute("width", "10000");
    mask.setAttribute("height", "10000");

    // White background = lines visible
    const maskBg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    maskBg.setAttribute("x", "-5000");
    maskBg.setAttribute("y", "-5000");
    maskBg.setAttribute("width", "10000");
    maskBg.setAttribute("height", "10000");
    maskBg.setAttribute("fill", "white");
    mask.appendChild(maskBg);

    defs.appendChild(mask);
    svg.appendChild(defs);

    eNode.element.insertBefore(svg, eNode.element.firstChild);
    eNode._linesSvg = svg;
    eNode._linesMask = mask;
    eNode._maskRects = [];
    eNode._lines = [];

    // Mouse move handler
    const handleMouseMove = (e) => {
      eNode._hasMouseInput = true;
      const rect = eNode.element.getBoundingClientRect();
      eNode._mouseX = e.clientX - rect.left;
      eNode._mouseY = e.clientY - rect.top;

    };

    eNode.element.addEventListener('mousemove', handleMouseMove);

    eNode._cleanupMouseMove = () => {
      eNode.element.removeEventListener('mousemove', handleMouseMove);
    };

    // Drag-to-pan state — touch requires a hold before drag activates so
    // quick swipes scroll normally. Mouse drags activate immediately.
    eNode._dragOffsetX = 0;
    eNode._dragOffsetY = 0;
    eNode._isDragging = false;
    eNode._dragActivated = false;
    eNode._dragStartX = null;
    eNode._dragStartY = null;
    eNode._dragStartOffsetX = 0;
    eNode._dragStartOffsetY = 0;
    eNode._dragResetActive = false;
    eNode._dragResetStartX = 0;
    eNode._dragResetStartY = 0;
    eNode._dragResetStartTime = 0;
    eNode._dragResetDuration = 400;
    eNode._lastTrackedCategory = null;
    eNode._lastTrackedCase = null;
    eNode._holdTimer = null;
    eNode._lastPointerY = 0;
    eNode._scrollParent = null;

    const DRAG_THRESHOLD = 5;
    const HOLD_DELAY = 300;
    const HOLD_MOVE_TOLERANCE = 10;

    const suppressClick = (e) => {
      e.stopPropagation();
      e.preventDefault();
    };

    const cancelHold = () => {
      if (eNode._holdTimer !== null) {
        clearTimeout(eNode._holdTimer);
        eNode._holdTimer = null;
      }
    };

    const findScrollParent = (el) => {
      let parent = el.parentElement;
      while (parent) {
        const overflow = getComputedStyle(parent).overflowY;
        if (overflow === 'scroll' || overflow === 'auto') return parent;
        parent = parent.parentElement;
      }
      return null;
    };

    const handlePointerDown = (e) => {
      eNode._dragStartX = e.clientX;
      eNode._dragStartY = e.clientY;
      eNode._dragStartOffsetX = eNode._dragOffsetX;
      eNode._dragStartOffsetY = eNode._dragOffsetY;
      eNode._isDragging = false;
      eNode._dragActivated = false;
      eNode._dragResetActive = false;
      eNode._lastPointerX = e.clientX;
      eNode._lastPointerY = e.clientY;

      cancelHold();

      if (e.pointerType === 'touch') {
        if (!eNode._scrollParent) {
          eNode._scrollParent = findScrollParent(eNode.element);
        }
        eNode._holdTimer = setTimeout(() => {
          eNode._holdTimer = null;
          eNode._dragActivated = true;
          eNode._dragStartX = eNode._lastPointerX;
          eNode._dragStartY = eNode._lastPointerY;
          eNode._dragStartOffsetX = eNode._dragOffsetX;
          eNode._dragStartOffsetY = eNode._dragOffsetY;
          eNode.element.style.cursor = 'grab';
        }, HOLD_DELAY);
      } else {
        eNode._dragActivated = true;
      }
    };

    const handlePointerMove = (e) => {
      if (eNode._dragStartX === null) return;

      // Touch before hold: forward movement as scroll on parent container
      if (!eNode._dragActivated) {
        if (eNode._scrollParent) {
          eNode._scrollParent.scrollTop += eNode._lastPointerY - e.clientY;
        }
        eNode._lastPointerX = e.clientX;
        eNode._lastPointerY = e.clientY;

        const dx = e.clientX - eNode._dragStartX;
        const dy = e.clientY - eNode._dragStartY;
        if (Math.abs(dx) > HOLD_MOVE_TOLERANCE || Math.abs(dy) > HOLD_MOVE_TOLERANCE) {
          cancelHold();
        }
        return;
      }

      const dx = e.clientX - eNode._dragStartX;
      const dy = e.clientY - eNode._dragStartY;

      if (!eNode._isDragging) {
        if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
          eNode._isDragging = true;
          eNode.element.setPointerCapture(e.pointerId);
          eNode.element.style.cursor = 'grabbing';
        }
        return;
      }

      eNode._dragOffsetX = eNode._dragStartOffsetX + dx;
      eNode._dragOffsetY = eNode._dragStartOffsetY + dy;

    };

    const handlePointerUp = (e) => {
      if (eNode._isDragging) {
        eNode.element.releasePointerCapture(e.pointerId);
        eNode.element.addEventListener('click', suppressClick, { capture: true, once: true });
      }
      cancelHold();
      eNode._isDragging = false;
      eNode._dragActivated = false;
      eNode._dragStartX = null;
      eNode.element.style.cursor = '';

    };

    const handlePointerCancel = () => {
      cancelHold();
      eNode._isDragging = false;
      eNode._dragActivated = false;
      eNode._dragStartX = null;
      eNode.element.style.cursor = '';

    };

    eNode.element.addEventListener('pointerdown', handlePointerDown);
    eNode.element.addEventListener('pointermove', handlePointerMove);
    eNode.element.addEventListener('pointerup', handlePointerUp);
    eNode.element.addEventListener('pointercancel', handlePointerCancel);

    eNode._cleanupDrag = () => {
      cancelHold();
      eNode.element.removeEventListener('pointerdown', handlePointerDown);
      eNode.element.removeEventListener('pointermove', handlePointerMove);
      eNode.element.removeEventListener('pointerup', handlePointerUp);
      eNode.element.removeEventListener('pointercancel', handlePointerCancel);
    };

    // Animation configuration
    const config = {
      parallaxStrength: 100,
      ellipseScale: 0.5,
      expansionScale: 1.5,
      categoryTransitionDuration: 800,
      caseAnimationDelay: 500,
      caseAnimationDuration: 600,
      caseAspectRatio: 3,  // Card width/height ratio for angle distribution (1 = uniform)
      caseEllipseScale: 1.0,  // Case orbit ellipse size relative to category ellipse
      deadZoneThreshold: 1.2,  // Width/height ratio above which no dead zones apply
      deadZoneStrength: Math.PI / 4,  // Maximum dead zone angle (radians)
      deadZoneCurve: 1.0,  // Ramp curve (1 = linear, >1 = slow start, <1 = fast start)
      portraitVerticalBoost: 0.5,  // Max extra vertical scale for case orbit on portrait screens
      snapToPixel: window.devicePixelRatio === 1
    };

    // Category transition state (for positioning animation)
    eNode._categoryTransition = {
      startTime: null,
      duration: 0,
      previousCategory: null,  // uid of previously selected category (or null)
      currentCategory: null    // uid of currently selected category (or null)
    };

    // Create GUI
    // const gui = new GUI({ title: 'Network Animation', width: 300 });
    // gui.domElement.style.position = 'fixed';
    // gui.domElement.style.top = '10px';
    // gui.domElement.style.right = '10px';

    // gui.add(config, 'parallaxStrength', 0, 500).name('Parallax Strength');

    // const layoutFolder = gui.addFolder('Layout');
    // layoutFolder.add(config, 'maxWidth', 200, 1500).name('Max Width');
    // layoutFolder.add(config, 'maxHeight', 200, 1200).name('Max Height');
    // layoutFolder.add(config, 'expansionScale', 1.0, 2.0).name('Expansion Scale');

    // const timingFolder = gui.addFolder('Timing');
    // timingFolder.add(config, 'categoryTransitionDuration', 100, 2000).name('Category (ms)');
    // timingFolder.add(config, 'caseAnimationDelay', 0, 2000).name('Case Delay (ms)');
    // timingFolder.add(config, 'caseAnimationDuration', 100, 2000).name('Cases (ms)');

    // eNode._gui = gui;
    eNode._animationConfig = config;

    // Calculate category positions after a delay to ensure child nodes are created by component
    setTimeout(() => this.calculateCategoryPositions(eNode), 50);
  }

  /**
   * Store category UIDs and a random start angle.
   * Actual positions are computed each frame via equal arc-length distribution.
   */
  calculateCategoryPositions(eNode) {
    const node = nodes.get(eNode.uid);
    if (!node._categoryNodes || node._categoryNodes.length === 0) return;

    eNode._categoryUids = node._categoryNodes.map(c => c.node.uid);
    eNode._randomStartAngle = Math.random() * Math.PI * 2;

    // Initialize positions — will be overwritten in first animate() frame
    eNode._categoryUids.forEach(uid => {
      eNode._categoryPositions.set(uid, { x: 0, y: 0 });
    });
  }

  /**
   * Distribute categories at equal effective-distance intervals on an ellipse.
   * Accounts for tile dimensions — wider tiles consume more space along the tangent,
   * so categories spread further apart where tiles are wide relative to the curve.
   *
   * @param {number} numCategories - Number of categories to place
   * @param {number} radiusX - Ellipse horizontal radius (pixels)
   * @param {number} radiusY - Ellipse vertical radius (pixels)
   * @param {number} startAngle - Random rotation offset
   * @param {number} tileWidth - Average tile width (pixels)
   * @param {number} tileHeight - Average tile height (pixels)
   * @returns {number[]} - Angles for each category
   */
  distributeEqualArcLength(numCategories, radiusX, radiusY, startAngle, tileWidth, tileHeight) {
    if (numCategories === 0) return [];
    if (numCategories === 1) return [startAngle];

    const steps = 720;
    const dTheta = (Math.PI * 2) / steps;
    const cumulative = [0];
    let totalCost = 0;

    for (let i = 0; i < steps; i++) {
      const theta = startAngle + i * dTheta;
      // Ellipse tangent vector at this angle
      const tx = -radiusX * Math.sin(theta);
      const ty = radiusY * Math.cos(theta);
      const tangentLength = Math.sqrt(tx * tx + ty * ty);

      // Arc length for this step
      const arcSegment = tangentLength * dTheta;

      // Tile footprint projected onto tangent direction
      // Tangent unit vector: (tx/tangentLength, ty/tangentLength)
      // Projected tile size = w*|unitTx| + h*|unitTy| (bounding box projection)
      const unitTx = tx / tangentLength;
      const unitTy = ty / tangentLength;
      const projectedTile = tileWidth * Math.abs(unitTx) + tileHeight * Math.abs(unitTy);

      // Cost = arc distance + tile footprint scaled per category
      // Divide tile footprint by numCategories so total tile cost = one tile per slot
      totalCost += arcSegment + (projectedTile * dTheta / (Math.PI * 2)) * numCategories;
      cumulative.push(totalCost);
    }

    // Place categories at equal cost intervals
    const costPerCategory = totalCost / numCategories;
    const angles = [];

    for (let i = 0; i < numCategories; i++) {
      const targetCost = i * costPerCategory;

      let lo = 0;
      let hi = steps;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (cumulative[mid] < targetCost) lo = mid + 1;
        else hi = mid;
      }

      const j = Math.max(0, lo - 1);
      const segmentCost = cumulative[j + 1] - cumulative[j];
      const t = segmentCost > 0 ? (targetCost - cumulative[j]) / segmentCost : 0;
      const angle = startAngle + (j + t) * dTheta;

      angles.push(angle);
    }

    return angles;
  }

  setEnableAnimations(eNode) {
    if (eNode.props.enableAnimations && !eNode._animationFrameId) {
      // Delay to ensure children are created
      setTimeout(() => this.startAnimations(eNode), 100);
    } else if (!eNode.props.enableAnimations && eNode._animationFrameId) {
      cancelAnimationFrame(eNode._animationFrameId);
      eNode._animationFrameId = null;
    }
  }

  startAnimations(eNode) {
    // Get the framework node
    const node = nodes.get(eNode.uid);

    // Get all child category nodes
    const childrenMap = nodes.getChildrenFor(eNode.uid);
    const categoryUids = [];

    for (const [uid, childNode] of childrenMap) {
      if (childNode.namespace === "network:category") {
        const childENode = visualNodes.get(uid);
        if (childENode?.element) {
          // Get the plate DOM element to read its rendered dimensions
          const plateNode = childNode._ids["plate"];
          const plateENode = visualNodes.get(plateNode?.uid);

          // Create mask rect for this card (black = hide lines)
          const maskRect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
          maskRect.setAttribute("fill", "black");
          maskRect.setAttribute("rx", "24"); // Match border radius
          eNode._linesMask.appendChild(maskRect);

          // POSITIONING: Read stored position from environment (BROWSER PIXELS)
          // These are the base layout positions calculated in createCategories
          const position = eNode._categoryPositions.get(uid) || { x: 0, y: 0 };

          eNode._categoryElements.set(uid, {
            element: childENode.element,
            plateElement: plateENode?.element || null,
            maskRect: maskRect,
            // Base position (static, from elliptical layout)
            baseX: position.x,
            baseY: position.y,
            // Timed animation: start -> target with easing
            startX: position.x,
            startY: position.y,
            targetX: position.x,
            targetY: position.y,
            animatedX: position.x,
            animatedY: position.y,
            depth: 0.75 + Math.random() * 0.25,
            // Case animation state (independent per category)
            previousSelected: false,
            caseAnimationStartTime: null,
            caseAnimationDirection: null // 'expand' or 'collapse'
          });

          categoryUids.push(uid);
        }
      }
    }

    // Create circular chain of lines (masked so they hide behind cards)
    const linesGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
    linesGroup.setAttribute("mask", "url(#card-mask)");
    eNode._linesSvg.appendChild(linesGroup);
    eNode._linesGroup = linesGroup; // Store reference for case lines

    for (let i = 0; i < categoryUids.length; i++) {
      const currentUid = categoryUids[i];
      const nextUid = categoryUids[(i + 1) % categoryUids.length];

      const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
      line.setAttribute("stroke", "white");
      line.setAttribute("stroke-width", "1");
      linesGroup.appendChild(line);

      eNode._lines.push({
        element: line,
        fromUid: currentUid,
        toUid: nextUid
      });
    }

    // Initialize expansion state for all categories (cases always visible)
    categoryUids.forEach(uid => {
      eNode._categoryExpansionState.set(uid, {
        caseAngles: null,
        caseAnimationData: new Map()
      });
    });

    this.animate(eNode);
  }

  /**
   * Calculate target position for a category based on current selection state
   */
  calculateCategoryTarget(eNode, data, selectedCategoryUid, scaleFactorX, scaleFactorY) {
    const config = eNode._animationConfig;
    const expansionScale = selectedCategoryUid ? (config?.expansionScale || 1.5) : 1.0;

    // Start with base position scaled by current layout factors
    let targetX = data.baseX * scaleFactorX;
    let targetY = data.baseY * scaleFactorY;

    if (selectedCategoryUid) {
      // Get the selected category's base position
      const selectedData = eNode._categoryElements.get(selectedCategoryUid);
      if (selectedData) {
        const selectedBaseX = selectedData.baseX * scaleFactorX;
        const selectedBaseY = selectedData.baseY * scaleFactorY;

        // Center offset: shift so selected category is at center
        const centerOffsetX = -selectedBaseX;
        const centerOffsetY = -selectedBaseY;

        // Apply center offset then expansion scale
        targetX = (data.baseX * scaleFactorX + centerOffsetX) * expansionScale;
        targetY = (data.baseY * scaleFactorY + centerOffsetY) * expansionScale;
      }
    }

    return { x: targetX, y: targetY };
  }

  /**
   * Trigger a new category transition - captures current positions and sets targets
   */
  triggerCategoryTransition(eNode, newCategoryUid, scaleFactorX, scaleFactorY) {
    const config = eNode._animationConfig;
    const transition = eNode._categoryTransition;

    // Fold drag offset into start positions so everything animates as one motion
    eNode._categoryElements.forEach((data) => {
      data.startX = data.animatedX + eNode._dragOffsetX;
      data.startY = data.animatedY + eNode._dragOffsetY;

      // Calculate new target based on new selection (no drag offset)
      const target = this.calculateCategoryTarget(eNode, data, newCategoryUid, scaleFactorX, scaleFactorY);
      data.targetX = target.x;
      data.targetY = target.y;
    });

    // Zero out drag offset immediately — it's now part of the transition
    eNode._dragOffsetX = 0;
    eNode._dragOffsetY = 0;
    eNode._dragResetActive = false;

    // Update transition state
    transition.previousCategory = transition.currentCategory;
    transition.currentCategory = newCategoryUid;
    transition.startTime = performance.now();
    transition.duration = config?.categoryTransitionDuration || 800;
  }

  animate(eNode) {
    if (!eNode.props.enableAnimations) return;

    if (eNode._isPaused) {
      eNode._animationFrameId = requestAnimationFrame(() => this.animate(eNode));
      return;
    }

    const containerWidth = eNode.element.clientWidth;
    const containerHeight = eNode.element.clientHeight;

    // Skip frame if element hasn't been laid out yet
    if (containerWidth === 0 || containerHeight === 0) {
      eNode._animationFrameId = requestAnimationFrame(() => this.animate(eNode));
      return;
    }

    // Get framework node for state
    const node = nodes.get(eNode.uid);

    eNode._time += 0.02;

    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;

    // Keep mouse centered when no real mouse input (mobile)
    if (!eNode._hasMouseInput) {
      eNode._mouseX = centerX;
      eNode._mouseY = centerY;
      if (isNaN(eNode._smoothMouseX)) {
        eNode._smoothMouseX = centerX;
        eNode._smoothMouseY = centerY;
      }
    }

    // Smooth mouse tracking — snap to target when close enough
    if (Math.abs(eNode._mouseX - eNode._smoothMouseX) < 0.5 &&
        Math.abs(eNode._mouseY - eNode._smoothMouseY) < 0.5) {
      eNode._smoothMouseX = eNode._mouseX;
      eNode._smoothMouseY = eNode._mouseY;
    } else {
      eNode._smoothMouseX += (eNode._mouseX - eNode._smoothMouseX) * 0.1;
      eNode._smoothMouseY += (eNode._mouseY - eNode._smoothMouseY) * 0.1;
    }

    const parallaxStrength = eNode._animationConfig?.parallaxStrength || 50;

    // Detect clicks (category or case change) and trigger drag reset
    if (node._currentCategory !== eNode._lastTrackedCategory ||
        node._selectedCaseNode !== eNode._lastTrackedCase) {
      eNode._lastTrackedCategory = node._currentCategory;
      eNode._lastTrackedCase = node._selectedCaseNode;
      if (eNode._dragOffsetX !== 0 || eNode._dragOffsetY !== 0) {
        eNode._dragResetActive = true;
        eNode._dragResetStartX = eNode._dragOffsetX;
        eNode._dragResetStartY = eNode._dragOffsetY;
        eNode._dragResetStartTime = performance.now();
      }
    }

    // Animate drag offset reset
    if (eNode._dragResetActive) {
      const elapsed = performance.now() - eNode._dragResetStartTime;
      const progress = Math.min(elapsed / eNode._dragResetDuration, 1);
      const eased = easeInOutCubic(progress);
      eNode._dragOffsetX = eNode._dragResetStartX * (1 - eased);
      eNode._dragOffsetY = eNode._dragResetStartY * (1 - eased);
      if (progress >= 1) {
        eNode._dragOffsetX = 0;
        eNode._dragOffsetY = 0;
        eNode._dragResetActive = false;
      }
    }
    const transition = eNode._categoryTransition;

    // Scale factors derive ellipse radii from container dimensions
    const ellipseScale = eNode._animationConfig?.ellipseScale || 0.5;
    const scaleFactorX = centerX * ellipseScale;
    const scaleFactorY = centerY * ellipseScale;

    // Recompute equal arc-length positions for current ellipse shape
    if (eNode._categoryUids) {
      // Measure average tile dimensions from plate elements
      let tileWidth = 0;
      let tileHeight = 0;
      let tileCount = 0;
      eNode._categoryElements.forEach((data) => {
        if (data.plateElement) {
          tileWidth += data.plateElement.offsetWidth;
          tileHeight += data.plateElement.offsetHeight;
          tileCount++;
        }
      });
      if (tileCount > 0) {
        tileWidth /= tileCount;
        tileHeight /= tileCount;
      }

      // Compute base offsets (startAngle=0) — the relative spacing between categories
      const baseAngles = this.distributeEqualArcLength(
        eNode._categoryUids.length, scaleFactorX, scaleFactorY, 0,
        tileWidth, tileHeight
      );

      // Find optimal rotation — recompute if tile dimensions changed
      // (first frames may have 0-size plates before DOM renders)
      const tileDimsKey = `${Math.round(tileWidth)}_${Math.round(tileHeight)}`;
      if (eNode._optimizedRotation === undefined || eNode._tileDimsKey !== tileDimsKey) {
        eNode._tileDimsKey = tileDimsKey;
        const numCategories = eNode._categoryUids.length;
        let bestRotation = eNode._randomStartAngle;
        let bestMinGap = -Infinity;
        const rotationSteps = 360;

        for (let r = 0; r < rotationSteps; r++) {
          const rotation = eNode._randomStartAngle + (r / rotationSteps) * Math.PI * 2;
          let minGap = Infinity;

          for (let a = 0; a < numCategories; a++) {
            const ya = Math.sin(baseAngles[a] + rotation) * scaleFactorY;
            for (let b = a + 1; b < numCategories; b++) {
              const yb = Math.sin(baseAngles[b] + rotation) * scaleFactorY;
              const gap = Math.abs(ya - yb);
              if (gap < minGap) minGap = gap;
            }
          }

          if (minGap > bestMinGap) {
            bestMinGap = minGap;
            bestRotation = rotation;
          }
        }

        eNode._optimizedRotation = bestRotation;
      }

      // Apply cached rotation to get final angles
      const angles = baseAngles.map(a => a + eNode._optimizedRotation);

      eNode._categoryUids.forEach((uid, index) => {
        const angle = angles[index];
        const data = eNode._categoryElements.get(uid);
        if (data) {
          data.baseX = Math.cos(angle);
          data.baseY = Math.sin(angle);
        }
        eNode._categoryPositions.set(uid, {
          x: Math.cos(angle),
          y: Math.sin(angle)
        });
      });
    }

    // Detect category selection change and trigger transition
    if (node._currentCategory !== transition.currentCategory) {
      this.triggerCategoryTransition(eNode, node._currentCategory, scaleFactorX, scaleFactorY);
    }

    // Calculate animation progress for category positioning
    let progress = 1; // Default to complete
    if (transition.startTime !== null) {
      const elapsed = performance.now() - transition.startTime;
      progress = Math.min(elapsed / transition.duration, 1);

      if (progress >= 1) {
        transition.startTime = null;
      }
    }

    const easedProgress = easeInOutCubic(progress);

    // Store positions for line updates
    const positions = new Map();

    // Animate each category position
    eNode._categoryElements.forEach((data, uid) => {
      // When no transition is active, continuously update target from current scale factors
      if (transition.startTime === null) {
        const target = this.calculateCategoryTarget(eNode, data, node._currentCategory, scaleFactorX, scaleFactorY);
        data.targetX = target.x;
        data.targetY = target.y;
        data.animatedX = target.x;
        data.animatedY = target.y;
      } else {
        // Continuously update targets to track container dimension changes
        const target = this.calculateCategoryTarget(eNode, data, node._currentCategory, scaleFactorX, scaleFactorY);
        data.targetX = target.x;
        data.targetY = target.y;
        // Interpolate from start to target using eased progress
        data.animatedX = data.startX + (data.targetX - data.startX) * easedProgress;
        data.animatedY = data.startY + (data.targetY - data.startY) * easedProgress;
      }

      // Apply parallax continuously
      const parallaxX = -((eNode._smoothMouseX - centerX) / centerX) * parallaxStrength * data.depth;
      const parallaxY = -((eNode._smoothMouseY - centerY) / centerY) * parallaxStrength * data.depth;

      let x = data.animatedX + parallaxX + eNode._dragOffsetX;
      let y = data.animatedY + parallaxY + eNode._dragOffsetY;
      if (eNode._animationConfig?.snapToPixel) { x = Math.round(x); y = Math.round(y); }

      // Apply final position via CSS transform
      data.element.style.transform = `translate(${x}px, ${y}px)`;
      data.element.style.zIndex = (uid === node._currentCategory) ? "10" : "0";

      // Get plate dimensions from DOM element
      const plateWidth = data.plateElement ? data.plateElement.offsetWidth : 0;
      const plateHeight = data.plateElement ? data.plateElement.offsetHeight : 0;

      positions.set(uid, { x, y });

      // Update mask rect position
      if (data.maskRect && data.plateElement) {
        data.maskRect.setAttribute("x", centerX + x - plateWidth / 2);
        data.maskRect.setAttribute("y", centerY + y - plateHeight / 2);
        data.maskRect.setAttribute("width", plateWidth);
        data.maskRect.setAttribute("height", plateHeight);
      }
    });

    // Update line positions
    eNode._lines.forEach((lineData) => {
      const fromPos = positions.get(lineData.fromUid);
      const toPos = positions.get(lineData.toUid);

      if (fromPos && toPos) {
        lineData.element.setAttribute("x1", centerX + fromPos.x);
        lineData.element.setAttribute("y1", centerY + fromPos.y);
        lineData.element.setAttribute("x2", centerX + toPos.x);
        lineData.element.setAttribute("y2", centerY + toPos.y);
      }
    });

    // Handle case depth/expansion animations per category
    eNode._categoryElements.forEach((data, categoryUid) => {
      const categoryNode = nodes.get(categoryUid);
      const isSelected = categoryNode?.selected || false;

      // Detect selection change
      if (isSelected !== data.previousSelected) {
        data.previousSelected = isSelected;
        data.caseAnimationStartTime = performance.now();
        data.caseAnimationDirection = isSelected ? 'expand' : 'collapse';
      }

      // Calculate expansion progress (0 = depth, 1 = full)
      let expansionProgress = isSelected ? 1 : 0;
      if (data.caseAnimationStartTime !== null) {
        const elapsed = performance.now() - data.caseAnimationStartTime;
        const duration = eNode._animationConfig?.caseAnimationDuration || 600;
        const rawProgress = Math.min(elapsed / duration, 1);
        const eased = easeInOutCubic(rawProgress);

        expansionProgress = data.caseAnimationDirection === 'expand' ? eased : 1 - eased;

        if (rawProgress >= 1) {
          data.caseAnimationStartTime = null;
        }
      }

      this.animateCasesForCategory(eNode, node, positions, categoryUid, expansionProgress, scaleFactorX, scaleFactorY, containerWidth, containerHeight);
    });

    eNode._animationFrameId = requestAnimationFrame(() => this.animate(eNode));
  }

  /**
   * Animate cases during collapse - uses reverse progress from category transition
   */
  animateCasesCollapse(eNode, node, positions, categoryUid, reverseProgress, scaleFactorX, scaleFactorY, containerWidth, containerHeight) {
    const categoryState = eNode._categoryExpansionState.get(categoryUid);
    if (!categoryState || !categoryState.caseAnimationData.size) {
      return;
    }

    const cfg = eNode._animationConfig;
    const deadZone = calculateDeadZone(
      containerWidth, containerHeight,
      cfg.deadZoneThreshold, cfg.deadZoneStrength, cfg.deadZoneCurve
    );
    const ratio = containerWidth / containerHeight;
    let portraitT = ratio < cfg.deadZoneThreshold ? (cfg.deadZoneThreshold - ratio) / cfg.deadZoneThreshold : 0;
    portraitT = Math.pow(Math.min(Math.max(portraitT, 0), 1), cfg.deadZoneCurve);
    const verticalBoost = 1 + portraitT * cfg.portraitVerticalBoost;

    categoryState.caseAnimationData.forEach((animData, caseUid) => {
      const categoryPos = positions.get(animData.categoryUid);
      if (!categoryPos) return;

      // Recompute elliptical position from angle and current scale factors
      const caseScale = eNode._animationConfig?.caseEllipseScale || 1.0;
      const remappedAngle = remapAngleDeadZone(animData.angle, deadZone);
      animData.baseX = Math.cos(remappedAngle) * scaleFactorX * caseScale;
      animData.baseY = Math.sin(remappedAngle) * scaleFactorY * caseScale * verticalBoost;

      // Animate case position in reverse (baseX/baseY -> 0)
      animData.animatedX = animData.baseX * reverseProgress;
      animData.animatedY = animData.baseY * reverseProgress;

      let transformX = animData.animatedX;
      let transformY = animData.animatedY;
      if (eNode._animationConfig?.snapToPixel) { transformX = Math.round(transformX); transformY = Math.round(transformY); }

      const caseX = categoryPos.x + transformX;
      const caseY = categoryPos.y + transformY;

      // Update line
      const containerCenterX = containerWidth / 2;
      const containerCenterY = containerHeight / 2;
      animData.line.setAttribute("x1", containerCenterX + categoryPos.x);
      animData.line.setAttribute("y1", containerCenterY + categoryPos.y);
      animData.line.setAttribute("x2", containerCenterX + caseX);
      animData.line.setAttribute("y2", containerCenterY + caseY);

      // Calculate line length
      const dx = caseX - categoryPos.x;
      const dy = caseY - categoryPos.y;
      const lineLength = Math.sqrt(dx * dx + dy * dy);

      // Un-draw line with reverse progress
      animData.line.setAttribute("stroke-dasharray", lineLength);
      animData.line.setAttribute("stroke-dashoffset", lineLength * (1 - reverseProgress));
      if (reverseProgress <= 0.01) {
        animData.line.setAttribute("opacity", "0");
      }

      // Scale cases in reverse (1 -> 0.01)
      const currentScale = 0.01 + (reverseProgress * 0.99);
      animData.lastScale = currentScale;

      if (animData.element) {
        animData.element.style.transform = `translate(${transformX}px, ${transformY}px) scale(${currentScale})`;
      }

      // Update mask rect position during collapse (offsetWidth doesn't include CSS scale)
      if (animData.maskRect && animData.plateElement) {
        const plateWidth = animData.plateElement.offsetWidth * currentScale;
        const plateHeight = animData.plateElement.offsetHeight * currentScale;
        animData.maskRect.setAttribute("x", containerCenterX + caseX - plateWidth / 2);
        animData.maskRect.setAttribute("y", containerCenterY + caseY - plateHeight / 2);
        animData.maskRect.setAttribute("width", plateWidth);
        animData.maskRect.setAttribute("height", plateHeight);
      }
    });
  }

  animateCasesForCategory(eNode, node, positions, categoryUid, easedCaseProgress, scaleFactorX, scaleFactorY, containerWidth, containerHeight) {
    // Case nodes stored per category
    const categoryCases = node._casesPerCategory?.get(categoryUid);
    if (!categoryCases || categoryCases.length === 0) {
      return;
    }

    // Get the category state for this category
    if (!eNode._categoryExpansionState.has(categoryUid)) {
      return;
    }
    const categoryState = eNode._categoryExpansionState.get(categoryUid);
    const aspectRatio = eNode._animationConfig?.caseAspectRatio || 2.25;

    // Calculate case angles once per category
    if (!categoryState.caseAngles) {
      const expandedData = eNode._categoryElements.get(categoryUid);
      const numCases = categoryCases.length;

      // Find the two neighboring category angles
      const neighborAngles = [];
      eNode._lines.forEach((lineData) => {
        if (lineData.fromUid === categoryUid) {
          const neighborData = eNode._categoryElements.get(lineData.toUid);
          if (neighborData) {
            const dx = neighborData.baseX - expandedData.baseX;
            const dy = neighborData.baseY - expandedData.baseY;
            neighborAngles.push(Math.atan2(dy, dx));
          }
        } else if (lineData.toUid === categoryUid) {
          const neighborData = eNode._categoryElements.get(lineData.fromUid);
          if (neighborData) {
            const dx = neighborData.baseX - expandedData.baseX;
            const dy = neighborData.baseY - expandedData.baseY;
            neighborAngles.push(Math.atan2(dy, dx));
          }
        }
      });

      // Total spokes = cases + 2 category lines
      const totalSpokes = numCases + 2;
      const spokeAngle = (Math.PI * 2) / totalSpokes;

      // Create initial spoke positions (starting from angle 0)
      const initialSpokes = [];
      for (let i = 0; i < totalSpokes; i++) {
        initialSpokes.push(i * spokeAngle);
      }

      // Find closest spokes to neighbor angles and mark them as used
      const usedSpokes = new Set();
      neighborAngles.forEach((neighborAngle) => {
        // Normalize to 0-2π
        const normAngle = ((neighborAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        let closestIdx = 0;
        let closestDist = Infinity;
        initialSpokes.forEach((spoke, idx) => {
          if (usedSpokes.has(idx)) return;
          let dist = Math.abs(spoke - normAngle);
          if (dist > Math.PI) dist = Math.PI * 2 - dist;
          if (dist < closestDist) {
            closestDist = dist;
            closestIdx = idx;
          }
        });
        usedSpokes.add(closestIdx);
      });

      // Get remaining spoke indices for cases
      const caseSpokes = initialSpokes.filter((_, idx) => !usedSpokes.has(idx));

      // Sort neighbor angles and determine inside/outside arcs
      // Inside arc = toward center (smaller arc between the two neighbor lines)
      const angleToCenter = Math.atan2(-expandedData.baseY, -expandedData.baseX);
      const normCenter = ((angleToCenter % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);

      // Sort the two neighbor angles
      const sortedNeighbors = neighborAngles
        .map(a => ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2))
        .sort((a, b) => a - b);

      // Determine which arc contains the center direction (that's the inside arc)
      let insideArcStart, insideArcEnd, outsideArcStart, outsideArcEnd;
      if (sortedNeighbors.length === 2) {
        const arc1Start = sortedNeighbors[0];
        const arc1End = sortedNeighbors[1];
        const arc2Start = sortedNeighbors[1];
        const arc2End = sortedNeighbors[0] + Math.PI * 2;

        // Check if center is in arc1 (between sorted[0] and sorted[1])
        const centerInArc1 = normCenter > arc1Start && normCenter < arc1End;

        if (centerInArc1) {
          insideArcStart = arc1Start;
          insideArcEnd = arc1End;
          outsideArcStart = arc2Start;
          outsideArcEnd = arc2End;
        } else {
          insideArcStart = arc2Start;
          insideArcEnd = arc2End;
          outsideArcStart = arc1Start;
          outsideArcEnd = arc1End;
        }
      }

      // Calculate inside/outside arc spans for proportional distribution
      const insideSpan = insideArcEnd - insideArcStart;
      const outsideSpan = outsideArcEnd - outsideArcStart;
      const totalSpan = insideSpan + outsideSpan;

      // Distribute cases proportionally to arc span, with at least 1 inside
      let insideCount = Math.round((insideSpan / totalSpan) * numCases);
      if (insideCount === 0 && numCases > 0) insideCount = 1;
      const outsideCount = numCases - insideCount;

      // Use profile-aware distribution within each arc
      categoryState.caseAngles = [];

      if (insideCount > 0 && sortedNeighbors.length === 2) {
        const insideAngles = distributeByProfile(
          insideCount, insideArcStart, insideArcEnd, aspectRatio
        );
        categoryState.caseAngles.push(...insideAngles);
      }

      if (outsideCount > 0 && sortedNeighbors.length === 2) {
        const outsideAngles = distributeByProfile(
          outsideCount, outsideArcStart, outsideArcEnd, aspectRatio
        );
        categoryState.caseAngles.push(...outsideAngles);
      }

      // Fallback if no neighbors found
      if (categoryState.caseAngles.length === 0) {
        categoryState.caseAngles = distributeByProfile(
          numCases, 0, Math.PI * 2, aspectRatio
        );
      }
    }

    // POSITIONING: Calculate and apply case positions in BROWSER PIXELS
    const cfg = eNode._animationConfig;
    const deadZone = calculateDeadZone(
      containerWidth, containerHeight,
      cfg.deadZoneThreshold, cfg.deadZoneStrength, cfg.deadZoneCurve
    );
    const ratio = containerWidth / containerHeight;
    let portraitT = ratio < cfg.deadZoneThreshold ? (cfg.deadZoneThreshold - ratio) / cfg.deadZoneThreshold : 0;
    portraitT = Math.pow(Math.min(Math.max(portraitT, 0), 1), cfg.deadZoneCurve);
    const verticalBoost = 1 + portraitT * cfg.portraitVerticalBoost;

    categoryCases.forEach((caseInfo, index) => {
      const caseUid = caseInfo.node.uid;

      // Initialize animation data for this case
      if (!categoryState.caseAnimationData.has(caseUid)) {
        // Assign angle to this case
        const angle = categoryState.caseAngles[index % categoryState.caseAngles.length];

        // Create line (append to masked group so it hides behind cards)
        const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
        line.setAttribute("stroke", "white");
        line.setAttribute("stroke-width", "1");
        line.setAttribute("opacity", "0");
        eNode._linesGroup.appendChild(line);

        // Create mask rect for this case (black = hide lines behind card)
        const maskRect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        maskRect.setAttribute("fill", "black");
        maskRect.setAttribute("rx", "24");
        eNode._linesMask.appendChild(maskRect);

        categoryState.caseAnimationData.set(caseUid, {
          categoryUid: categoryUid,
          angle: angle,
          baseX: 0,
          baseY: 0,
          animatedX: 0,
          animatedY: 0,
          depth: 0.75 + Math.random() * 0.25,
          baseScale: 0.3 + Math.random() * 0.4,
          line,
          maskRect,
          element: null,
          plateElement: null,
          lastScale: 0.01
        });
      }

      const animData = categoryState.caseAnimationData.get(caseUid);

      // Recompute elliptical position from angle and current scale factors
      const caseScale = eNode._animationConfig?.caseEllipseScale || 1.0;
      const remappedAngle = remapAngleDeadZone(animData.angle, deadZone);
      animData.baseX = Math.cos(remappedAngle) * scaleFactorX * caseScale;
      animData.baseY = Math.sin(remappedAngle) * scaleFactorY * caseScale * verticalBoost;

      // Get the actual position of the category this case belongs to
      const categoryPos = positions.get(animData.categoryUid);
      if (!categoryPos) {
        return;
      }

      // Mobile: cases hidden when collapsed, animate from center to full on expand
      // Desktop: cases visible at half distance (depth), animate to full on expand
      const isMobile = env.mobile || env.tablet;
      const distanceFactor = isMobile
        ? easedCaseProgress
        : 0.5 + 0.5 * easedCaseProgress;
      animData.animatedX = animData.baseX * distanceFactor;
      animData.animatedY = animData.baseY * distanceFactor;

      // Per-case mouse parallax — smaller tiles (further away) move less
      const parallaxStrength = eNode._animationConfig?.parallaxStrength || 100;
      const centerX = containerWidth / 2;
      const centerY = containerHeight / 2;
      const mouseNormX = -((eNode._smoothMouseX - centerX) / centerX);
      const mouseNormY = -((eNode._smoothMouseY - centerY) / centerY);
      const caseParallaxX = mouseNormX * parallaxStrength * animData.baseScale * 0.5;
      const caseParallaxY = mouseNormY * parallaxStrength * animData.baseScale * 0.5;

      // Case transform = animated offset + parallax
      let transformX = animData.animatedX + caseParallaxX;
      let transformY = animData.animatedY + caseParallaxY;

      // Case position = category position + case offset
      let caseX = categoryPos.x + transformX;
      let caseY = categoryPos.y + transformY;
      if (eNode._animationConfig?.snapToPixel) { caseX = Math.round(caseX); caseY = Math.round(caseY); }

      // Lines need absolute container position
      const containerCenterX = containerWidth / 2;
      const containerCenterY = containerHeight / 2;

      // Line from category to case
      animData.line.setAttribute("x1", containerCenterX + categoryPos.x);
      animData.line.setAttribute("y1", containerCenterY + categoryPos.y);
      animData.line.setAttribute("x2", containerCenterX + caseX);
      animData.line.setAttribute("y2", containerCenterY + caseY);

      // Calculate line length for draw animation
      const dx = caseX - categoryPos.x;
      const dy = caseY - categoryPos.y;
      const lineLength = Math.sqrt(dx * dx + dy * dy);

      animData.line.setAttribute("opacity", isMobile && easedCaseProgress < 0.01 ? "0" : "1");
      animData.line.setAttribute("stroke-dasharray", lineLength);
      animData.line.setAttribute("stroke-dashoffset", "0");

      // Get element reference and re-parent to container for shared stacking context
      if (!animData.element) {
        const caseENode = visualNodes.get(caseUid);
        if (caseENode?.element) {
          animData.element = caseENode.element;
          // Move to container so all cases share the same z-index context
          eNode.element.appendChild(animData.element);
        }
      }

      // Cache plate and child element references
      if (!animData.plateElement) {
        const caseNode = nodes.get(caseUid);
        const plateNode = caseNode?._ids?.["plate"];
        if (plateNode) {
          const plateENode = visualNodes.get(plateNode.uid);
          if (plateENode?.element) {
            animData.plateElement = plateENode.element;
          }
        }
        const textNode = caseNode?._ids?.["case_name"];
        if (textNode) {
          const textENode = visualNodes.get(textNode.uid);
          if (textENode?.element) animData.textElement = textENode.element;
        }
        const iconNode = caseNode?._ids?.["icon"];
        if (iconNode) {
          const iconENode = visualNodes.get(iconNode.uid);
          if (iconENode?.element) animData.iconElement = iconENode.element;
        }
      }

      // Scale: desktop keeps random baseScale at depth, mobile hides completely
      const currentScale = isMobile
        ? easedCaseProgress
        : animData.baseScale + (1.0 - animData.baseScale) * easedCaseProgress;
      animData.lastScale = currentScale;

      const caseNode = nodes.get(caseUid);
      const isSelected = caseNode?.selected || false;

      if (animData.element) {
        // Use absolute position (re-parented to container, not category)
        animData.element.style.transform = `translate(${caseX}px, ${caseY}px) scale(${currentScale})`;
        const depthZ = Math.round(currentScale * 10) - 10;
        animData.element.style.zIndex = isSelected ? "20" : depthZ;
      }

      // Depth fade — skip when selected so component's selection styling takes effect
      if (!isSelected) {
        const depthAlpha = isMobile
          ? easedCaseProgress
          : animData.baseScale + (1.0 - animData.baseScale) * easedCaseProgress;
        if (animData.plateElement) {
          animData.plateElement.style.borderColor = `rgba(255,255,255,${depthAlpha})`;
          animData.plateElement.style.backgroundColor = `rgba(255,255,255,${0.1 * depthAlpha})`;
        }
        if (animData.textElement) {
          animData.textElement.style.color = `rgba(255,255,255,${depthAlpha})`;
        }
        if (animData.iconElement) {
          animData.iconElement.style.opacity = depthAlpha;
        }
      }


      // Update mask rect position (offsetWidth doesn't include CSS scale)
      if (animData.maskRect && animData.plateElement) {
        const plateWidth = animData.plateElement.offsetWidth * currentScale;
        const plateHeight = animData.plateElement.offsetHeight * currentScale;
        animData.maskRect.setAttribute("x", containerCenterX + caseX - plateWidth / 2);
        animData.maskRect.setAttribute("y", containerCenterY + caseY - plateHeight / 2);
        animData.maskRect.setAttribute("width", plateWidth);
        animData.maskRect.setAttribute("height", plateHeight);
      }
    });
  }

  clearCases(eNode) {
    const categoryUid = eNode.props.clear_cases?.categoryUid;

    // Get the category state
    const categoryState = eNode._categoryExpansionState.get(categoryUid);
    if (categoryState) {
      // Remove case lines and mask rects for this category from SVG
      categoryState.caseAnimationData.forEach((animData) => {
        if (animData.line) animData.line.remove();
        if (animData.maskRect) animData.maskRect.remove();
      });
      categoryState.caseAnimationData.clear();
      categoryState.caseAngles = null;
    }

    // Signal component to destroy case nodes for this category
    bridge.update({
      uid: eNode.uid,
      key: "destroy_cases",
      value: { categoryUid },
      sender: "browser"
    });
  }

  cleanupCases(eNode, node) {
    // Legacy method for collapse - now just calls clearCases
    this.clearCases(eNode);
  }

  destroy(eNode) {
    if (eNode._intersectionObserver) {
      eNode._intersectionObserver.disconnect();
      eNode._intersectionObserver = null;
    }

    if (eNode._animationFrameId) {
      cancelAnimationFrame(eNode._animationFrameId);
      eNode._animationFrameId = null;
    }

    if (eNode._cleanupMouseMove) {
      eNode._cleanupMouseMove();
      eNode._cleanupMouseMove = null;
    }

    if (eNode._cleanupDrag) {
      eNode._cleanupDrag();
      eNode._cleanupDrag = null;
    }

    // if (eNode._gui) {
    //   eNode._gui.destroy();
    //   eNode._gui = null;
    // }

    eNode._categoryElements?.clear();

    // Clean up all category expansion states
    if (eNode._categoryExpansionState) {
      eNode._categoryExpansionState.forEach((categoryState) => {
        categoryState.caseAnimationData.forEach((animData) => {
          if (animData.line) animData.line.remove();
          if (animData.maskRect) animData.maskRect.remove();
        });
        categoryState.caseAnimationData.clear();
      });
      eNode._categoryExpansionState.clear();
    }

    if (eNode._linesSvg) {
      eNode._linesSvg.remove();
      eNode._linesSvg = null;
    }

    eNode._lines = [];
    eNode._linesMask = null;
    eNode._maskRects = [];

    super.destroy(eNode);
  }
}

const networkContainerEnvironment = new NetworkContainerEnvironment("network:container");

export { NetworkContainerEnvironment, networkContainerEnvironment };
